import { Palette } from "lucide-react";
import { TEMPLATES, STYLES, ORIENTATIONS, COLOR_OPTIONS } from "./constants";

type DesignOptionsProps = {
  selectedTemplate: string | null;
  selectedStyle: string | null;
  selectedOrientation: string;
  selectedColors: string;
  onTemplateChange: (templateId: string) => void;
  onStyleChange: (styleId: string) => void;
  onOrientationChange: (orientationId: string) => void;
  onColorsChange: (colorsId: string) => void;
  showTitle?: boolean;
};

export const DesignOptions = ({
  selectedTemplate,
  selectedStyle,
  selectedOrientation,
  selectedColors,
  onTemplateChange,
  onStyleChange,
  onOrientationChange,
  onColorsChange,
  showTitle = true
}: DesignOptionsProps) => (
  <div>
    {showTitle && (
      <h2 className="mb-4 flex items-center gap-2 text-base font-semibold">
        <Palette className="h-4 w-4 text-indigo-600" />
        Design Options
      </h2>
    )}

    {/* Templates Section */}
    <div className="mb-4">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
        Design Type
      </h3>
      <div className="grid grid-cols-2 gap-1">
        {TEMPLATES.map(template => (
          <button
            key={template.id}
            data-testid={`template-${template.id}-button`}
            onClick={() => onTemplateChange(template.id)}
            className={`flex items-center gap-1.5 rounded border px-2 py-1.5 text-left text-xs transition-all duration-200 ${
              selectedTemplate === template.id
                ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <span className="text-sm">{template.icon}</span>
            <span className="truncate font-medium">{template.name}</span>
          </button>
        ))}
      </div>
    </div>

    {/* Styles Section */}
    <div className="mb-4">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">Art Style</h3>
      <div className="grid grid-cols-2 gap-1">
        {STYLES.map(style => (
          <button
            key={style.id}
            data-testid={`style-${style.id}-button`}
            onClick={() => onStyleChange(style.id)}
            className={`rounded border px-2 py-1.5 text-xs font-medium transition-all duration-200 ${
              selectedStyle === style.id
                ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-1.5">
              <div className={`h-2 w-2 rounded-full ${style.color}`} />
              <span>{style.name}</span>
            </div>
          </button>
        ))}
      </div>
    </div>

    {/* Orientation Section */}
    <div className="mb-4">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
        Orientation
      </h3>
      <div className="grid grid-cols-3 gap-1">
        {ORIENTATIONS.map(orientation => {
          const Icon = orientation.icon;
          return (
            <button
              key={orientation.id}
              data-testid={`orientation-${orientation.id}-button`}
              onClick={() => onOrientationChange(orientation.id)}
              className={`flex flex-col items-center gap-0.5 rounded border px-2 py-1.5 text-xs transition-all duration-200 ${
                selectedOrientation === orientation.id
                  ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="text-xs font-medium">{orientation.name}</span>
            </button>
          );
        })}
      </div>
    </div>

    {/* Color Options Section */}
    <div>
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
        Color Mode
      </h3>
      <div className="grid grid-cols-2 gap-1">
        {COLOR_OPTIONS.map(color => (
          <button
            key={color.id}
            data-testid={`color-${color.id}-button`}
            onClick={() => onColorsChange(color.id)}
            className={`rounded border px-2 py-1.5 text-center text-xs font-medium transition-all duration-200 ${
              selectedColors === color.id
                ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            {color.name}
          </button>
        ))}
      </div>
    </div>
  </div>
);
